// src/pages/JourneyMap/phases/Step5Sales.jsx
// Passo 5 — VENDAS: dashboard completo com gráfico, termômetro, leads e dica da Edy
import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, BarChart, Bar,
} from 'recharts';
import EdyAssistant from '../components/EdyAssistant';
import apiClient from '../../../lib/apiClient';
import { LightBulbIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

// Tooltip customizado para estilo dark
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(13,13,26,.9)',
      border: '1px solid rgba(34,211,238,.25)',
      borderRadius: 8,
      padding: '8px 14px',
      fontSize: '0.82rem',
    }}>
      <p style={{ color: '#94a3b8', marginBottom: 4 }}>{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color, margin: '2px 0' }}>
          {p.name}: <strong>{p.value.toLocaleString('pt-BR')}</strong>
        </p>
      ))}
    </div>
  );
}

export default function Step5Sales({ campaignId, onRepeat }) {
  const [data,       setData]    = useState(null);
  const [loading,    setLoading] = useState(true);
  const [error,      setError]   = useState('');

  useEffect(() => {
    loadDashboard();
  }, [campaignId]); // eslint-disable-line

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await apiClient.request(`/mp/journey/${campaignId}/dashboard`);
      const d = await r.json();
      if (d.success) {
        setData(d);
      } else {
        setError(d.message || 'Não foi possível carregar o dashboard.');
      }
    } catch {
      setError('Erro de conexão ao carregar resultados.');
    } finally {
      setLoading(false);
    }
  };

  const perf       = data || {};
  // Converte arrays paralelos → array de objetos para Recharts
  const dates      = perf.performance?.dates       || [];
  const imprArr    = perf.performance?.impressions || [];
  const clickArr   = perf.performance?.clicks      || [];
  const leadArr    = perf.performance?.leads       || [];
  const dailyPerf  = dates.map((day, i) => ({
    day,
    impressions: Number(imprArr[i] || 0),
    clicks:      Number(clickArr[i] || 0),
    leads:       Number(leadArr[i]  || 0),
  }));
  const kpis       = perf.totals       || {};
  const tip        = perf.optimization_tip || '';
  const totalImpr  = Number(kpis.impressions || 0);
  const totalClick = Number(kpis.clicks      || 0);
  const totalLeads = Number(kpis.leads       || 0);
  const ctr        = totalImpr > 0 ? ((totalClick / totalImpr) * 100).toFixed(2) : '0.00';
  const goalClicks = 1000;
  const thermoFill = Math.min(100, Math.round((totalClick / goalClicks) * 100));

  const edyMsg = tip
    ? `Dica campeã: ${tip}`
    : `Seus anúncios estão no ar! ${totalClick.toLocaleString('pt-BR')} cliques até agora — você está voando!`;

  if (loading) {
    return (
      <div className="jm-container">
        <EdyAssistant message="Carregando seus resultados..." mood="think" loading />
        <div className="jm-card jm-loading">
          <div className="jm-spinner" />
          <span>Obtendo dados de performance...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="jm-container">
        <EdyAssistant message="Ops! Tive problema em carregar os resultados." mood="think" />
        <div className="jm-card" style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--edy-red)', marginBottom: 16 }}>{error}</p>
          <button className="jm-btn-outline" onClick={loadDashboard}>Tentar novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div className="jm-container">
      <EdyAssistant message={edyMsg} mood="celebrate" />

      {/* Dica de otimização */}
      {tip && (
        <div className="jm-tip-banner">
          <span className="jm-tip-icon"><LightBulbIcon style={{ width: 18, height: 18 }} /></span>
          <div>
            <strong>Dica do Eddy:</strong>
            <p style={{ margin: '2px 0 0', opacity: .85 }}>{tip}</p>
          </div>
        </div>
      )}

      {/* KPI Grid */}
      <div className="jm-kpi-grid">
        {[
          { label: 'Impressões',  value: totalImpr.toLocaleString('pt-BR'),  color: '#22d3ee' },
          { label: 'Cliques',     value: totalClick.toLocaleString('pt-BR'), color: '#a855f7' },
          { label: 'CTR',         value: `${ctr}%`,                          color: '#f97316' },
          { label: 'Leads',       value: totalLeads.toLocaleString('pt-BR'), color: '#4ade80' },
        ].map(k => (
          <div key={k.label} className="jm-kpi-card" style={{ '--kpi-color': k.color }}>
            <span className="jm-kpi-value">{k.value}</span>
            <span className="jm-kpi-label">{k.label}</span>
          </div>
        ))}
      </div>

      {/* Termômetro de cliques */}
      <div className="jm-card">
        <p style={{ fontSize: '0.82rem', color: 'var(--edy-muted)', marginBottom: 8 }}>
          Termômetro de cliques — Meta: {goalClicks.toLocaleString('pt-BR')}
        </p>
        <div className="jm-thermometer">
          <div className="jm-thermo-bar" style={{ width: `${thermoFill}%` }} />
        </div>
        <p style={{ fontSize: '0.78rem', color: 'var(--edy-muted)', textAlign: 'right', marginTop: 4 }}>
          {thermoFill}% da meta atingida
        </p>
      </div>

      {/* Gráfico de linha — impressões + cliques 7 dias */}
      {dailyPerf.length > 0 && (
        <div className="jm-card">
          <h4 className="jm-chart-title">Performance dos últimos 7 dias</h4>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={dailyPerf} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.05)" />
              <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
              <Line type="monotone" dataKey="impressions" name="Impressões" stroke="#22d3ee" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="clicks"      name="Cliques"    stroke="#a855f7" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="leads"       name="Leads"      stroke="#4ade80" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Barras de engajamento diário */}
      {dailyPerf.length > 0 && (
        <div className="jm-card">
          <h4 className="jm-chart-title">Cliques por dia</h4>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={dailyPerf} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.05)" />
              <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="clicks" name="Cliques" fill="url(#barGradient)" radius={[4, 4, 0, 0]} />
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#a855f7" stopOpacity={1}   />
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity={0.7} />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Leads por dia */}
      {dailyPerf.length > 0 && dailyPerf.some(d => d.leads > 0) && (
        <div className="jm-card">
          <h4 className="jm-chart-title">Leads por dia</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dailyPerf.filter(d => d.leads > 0).map((d, i) => (
              <div key={i} className="jm-lead-row">
                <span className="jm-lead-avatar">
                  {d.leads}
                </span>
                <div className="jm-lead-info">
                  <strong>{d.leads} lead{d.leads !== 1 ? 's' : ''}</strong>
                  <span> capturado{d.leads !== 1 ? 's' : ''}</span>
                </div>
                <span className="jm-lead-time">{d.day}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Repetir Sucesso */}
      <div className="jm-card" style={{ textAlign: 'center', padding: '24px 20px' }}>
        <p style={{ color: 'var(--edy-muted)', fontSize: '0.9rem', marginBottom: 16 }}>
          Sua campanha foi um sucesso! Quer criar uma nova jornada?
        </p>
        <button className="jm-btn-cta" onClick={onRepeat} style={{ minWidth: 220 }}>
          <ArrowPathIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 6 }} />Repetir Sucesso — Nova Jornada
        </button>
      </div>
    </div>
  );
}
